import { useEffect, useState } from "react";
import type { ActivityRegistrationOptionsConfig } from "../../../utils/activityRegistrationOptions";
import {
  applyAgeMaxInput,
  applyAgeMinInput,
  choiceAgeLimitEditing,
  choiceAgeMaxDisplayText,
  choiceAgeMinDisplayText,
  choiceGenderLimitEditing,
  choiceHasAgeRestriction,
  choiceHasGenderRestriction,
  choiceHasParticipantAudienceRestriction,
  choiceParticipantAudienceLimitEditing,
  clearAgeLimitEditing,
  clearAgeLimitValues,
  clearGenderLimitEditing,
  clearParticipantAudienceLimitEditing,
  emptyRegistrationOptionChoice,
  emptyRegistrationOptionQuestion,
  enableAgeLimitEditing,
  enableGenderLimitEditing,
  enableParticipantAudienceLimitEditing,
  MAX_REGISTRATION_OPTION_CHOICES,
  MAX_REGISTRATION_OPTION_QUESTIONS,
  normalizeDigitYuanInput,
  validateRegistrationOptionsForm,
} from "../../../utils/activityRegistrationOptions";
import { reportError } from "../../../api/errors";

function cloneRegistrationOptions(cfg: ActivityRegistrationOptionsConfig): ActivityRegistrationOptionsConfig {
  return {
    enabled: cfg.enabled,
    items: cfg.items.map((q) => ({
      ...q,
      choices: q.choices.map((c) => ({ ...c })),
    })),
  };
}

interface Props {
  open: boolean;
  initialValue: ActivityRegistrationOptionsConfig;
  onClose: () => void;
  onSave: (value: ActivityRegistrationOptionsConfig) => void;
}

export default function ActivityRegistrationOptionsEditorModal({ open, initialValue, onClose, onSave }: Props) {
  const [draft, setDraft] = useState<ActivityRegistrationOptionsConfig>(() => cloneRegistrationOptions(initialValue));

  useEffect(() => {
    if (!open) return;
    setDraft(cloneRegistrationOptions(initialValue));
  }, [open, initialValue]);

  if (!open) return null;

  const handleSave = () => {
    const toSave: ActivityRegistrationOptionsConfig = { ...draft, enabled: true };
    const err = validateRegistrationOptionsForm(toSave);
    if (err) {
      reportError(new Error(err), "无法保存");
      return;
    }
    onSave(toSave);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-[640px] max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0 pr-3">
            <h4 className="text-sm font-bold text-gray-900">报名活动选项</h4>
            <p className="text-xs text-gray-400 mt-0.5">逐题单选，可设收费与年龄/性别/参与对象</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {draft.items.map((q, qi) => (
            <div key={q.id} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-gray-700">题目 {qi + 1}</p>
                {draft.items.length > 1 ? (
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((p) => ({ ...p, items: p.items.filter((x) => x.id !== q.id) }))
                    }
                    className="text-xs font-semibold text-red-600 bg-white px-2 py-0.5 rounded-lg cursor-pointer hover:bg-red-50"
                  >
                    删除
                  </button>
                ) : null}
              </div>
              <input
                className="mb-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-teal-400"
                value={q.title}
                placeholder="题目标题，如：是否参加晚宴"
                maxLength={100}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    items: p.items.map((item) =>
                      item.id === q.id ? { ...item, title: e.target.value.slice(0, 100) } : item,
                    ),
                  }))
                }
              />
              {q.choices.map((c) => (
                <div key={c.id} className="mb-2 rounded-xl border border-gray-200 bg-white p-3">
                  <input
                    className="w-full rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:border-teal-400"
                    value={c.label}
                    placeholder="选项值文案"
                    maxLength={100}
                    onChange={(e) =>
                      setDraft((p) => ({
                        ...p,
                        items: p.items.map((item) =>
                          item.id === q.id
                            ? {
                                ...item,
                                choices: item.choices.map((ch) =>
                                  ch.id === c.id ? { ...ch, label: e.target.value.slice(0, 100) } : ch,
                                ),
                              }
                            : item,
                        ),
                      }))
                    }
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">收费</span>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((p) => ({
                          ...p,
                          items: p.items.map((item) =>
                            item.id === q.id
                              ? {
                                  ...item,
                                  choices: item.choices.map((ch) =>
                                    ch.id === c.id
                                      ? {
                                          ...ch,
                                          chargeEnabled: !ch.chargeEnabled,
                                          amountYuanText: !ch.chargeEnabled ? ch.amountYuanText : "",
                                        }
                                      : ch,
                                  ),
                                }
                              : item,
                          ),
                        }))
                      }
                      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                        c.chargeEnabled ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {c.chargeEnabled ? "是" : "否"}
                    </button>
                    {c.chargeEnabled ? (
                      <>
                        <span className="text-xs text-gray-400">金额</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-28 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1 text-sm focus:outline-none focus:border-teal-400"
                          value={c.amountYuanText}
                          placeholder="元"
                          onChange={(e) =>
                            setDraft((p) => ({
                              ...p,
                              items: p.items.map((item) =>
                                item.id === q.id
                                  ? {
                                      ...item,
                                      choices: item.choices.map((ch) =>
                                        ch.id === c.id
                                          ? { ...ch, amountYuanText: normalizeDigitYuanInput(e.target.value) }
                                          : ch,
                                      ),
                                    }
                                  : item,
                              ),
                            }))
                          }
                        />
                      </>
                    ) : null}
                    {q.choices.length > 2 ? (
                      <button
                        type="button"
                        onClick={() =>
                          setDraft((p) => ({
                            ...p,
                            items: p.items.map((item) =>
                              item.id === q.id
                                ? { ...item, choices: item.choices.filter((ch) => ch.id !== c.id) }
                                : item,
                            ),
                          }))
                        }
                        className="ml-auto text-xs text-red-500 cursor-pointer hover:text-red-600"
                      >
                        删选项
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-2 border-t border-gray-100 pt-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-gray-500">条件</span>
                      <button
                        type="button"
                        onClick={() =>
                          setDraft((p) => ({
                            ...p,
                            items: p.items.map((item) =>
                              item.id === q.id
                                ? {
                                    ...item,
                                    choices: item.choices.map((ch) =>
                                      ch.id === c.id
                                        ? choiceAgeLimitEditing(ch)
                                          ? clearAgeLimitEditing(ch)
                                          : enableAgeLimitEditing(ch)
                                        : ch,
                                    ),
                                  }
                                : item,
                            ),
                          }))
                        }
                        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                          choiceAgeLimitEditing(c) ? "bg-sky-100 text-sky-700" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        年龄
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDraft((p) => ({
                            ...p,
                            items: p.items.map((item) =>
                              item.id === q.id
                                ? {
                                    ...item,
                                    choices: item.choices.map((ch) =>
                                      ch.id === c.id
                                        ? choiceGenderLimitEditing(ch)
                                          ? clearGenderLimitEditing(ch)
                                          : enableGenderLimitEditing(ch)
                                        : ch,
                                    ),
                                  }
                                : item,
                            ),
                          }))
                        }
                        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                          choiceGenderLimitEditing(c) ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        性别
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDraft((p) => ({
                            ...p,
                            items: p.items.map((item) =>
                              item.id === q.id
                                ? {
                                    ...item,
                                    choices: item.choices.map((ch) =>
                                      ch.id === c.id
                                        ? choiceParticipantAudienceLimitEditing(ch)
                                          ? clearParticipantAudienceLimitEditing(ch)
                                          : enableParticipantAudienceLimitEditing(ch)
                                        : ch,
                                    ),
                                  }
                                : item,
                            ),
                          }))
                        }
                        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                          choiceParticipantAudienceLimitEditing(c)
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        参与对象
                      </button>
                      {!choiceHasAgeRestriction(c) &&
                      !choiceHasGenderRestriction(c) &&
                      !choiceHasParticipantAudienceRestriction(c) &&
                      !choiceAgeLimitEditing(c) &&
                      !choiceGenderLimitEditing(c) &&
                      !choiceParticipantAudienceLimitEditing(c) ? (
                        <span className="text-xs text-gray-400">不限</span>
                      ) : null}
                    </div>
                    {choiceAgeLimitEditing(c) ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input
                          type="number"
                          className="w-14 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1 text-sm text-center focus:outline-none focus:border-teal-400"
                          value={choiceAgeMinDisplayText(c)}
                          placeholder="下"
                          onChange={(e) =>
                            setDraft((p) => ({
                              ...p,
                              items: p.items.map((item) =>
                                item.id === q.id
                                  ? {
                                      ...item,
                                      choices: item.choices.map((ch) =>
                                        ch.id === c.id ? applyAgeMinInput(ch, e.target.value) : ch,
                                      ),
                                    }
                                  : item,
                              ),
                            }))
                          }
                        />
                        <span className="text-xs text-gray-400">—</span>
                        <input
                          type="number"
                          className="w-14 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1 text-sm text-center focus:outline-none focus:border-teal-400"
                          value={choiceAgeMaxDisplayText(c)}
                          placeholder="上"
                          onChange={(e) =>
                            setDraft((p) => ({
                              ...p,
                              items: p.items.map((item) =>
                                item.id === q.id
                                  ? {
                                      ...item,
                                      choices: item.choices.map((ch) =>
                                        ch.id === c.id ? applyAgeMaxInput(ch, e.target.value) : ch,
                                      ),
                                    }
                                  : item,
                              ),
                            }))
                          }
                        />
                        <span className="text-xs text-gray-500">周岁</span>
                        <button
                          type="button"
                          onClick={() =>
                            setDraft((p) => ({
                              ...p,
                              items: p.items.map((item) =>
                                item.id === q.id
                                  ? {
                                      ...item,
                                      choices: item.choices.map((ch) =>
                                        ch.id === c.id ? clearAgeLimitValues(ch) : ch,
                                      ),
                                    }
                                  : item,
                              ),
                            }))
                          }
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                            !choiceHasAgeRestriction(c) ? "bg-sky-100 text-sky-700" : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          不限
                        </button>
                      </div>
                    ) : null}
                    {choiceGenderLimitEditing(c) ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {(["男", "女"] as const).map((g) => (
                          <button
                            key={g}
                            type="button"
                            onClick={() =>
                              setDraft((p) => ({
                                ...p,
                                items: p.items.map((item) =>
                                  item.id === q.id
                                    ? {
                                        ...item,
                                        choices: item.choices.map((ch) =>
                                          ch.id === c.id ? { ...ch, gender: g } : ch,
                                        ),
                                      }
                                    : item,
                                ),
                              }))
                            }
                            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                              c.gender === g ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {g}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() =>
                            setDraft((p) => ({
                              ...p,
                              items: p.items.map((item) =>
                                item.id === q.id
                                  ? {
                                      ...item,
                                      choices: item.choices.map((ch) =>
                                        ch.id === c.id ? { ...ch, gender: null } : ch,
                                      ),
                                    }
                                  : item,
                              ),
                            }))
                          }
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                            !choiceHasGenderRestriction(c) ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          不限
                        </button>
                      </div>
                    ) : null}
                    {choiceParticipantAudienceLimitEditing(c) ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {(
                          [
                            { value: "member" as const, label: "会员" },
                            { value: "guest" as const, label: "散客" },
                          ] as const
                        ).map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() =>
                              setDraft((p) => ({
                                ...p,
                                items: p.items.map((item) =>
                                  item.id === q.id
                                    ? {
                                        ...item,
                                        choices: item.choices.map((ch) =>
                                          ch.id === c.id ? { ...ch, participantAudience: value } : ch,
                                        ),
                                      }
                                    : item,
                                ),
                              }))
                            }
                            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                              c.participantAudience === value
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() =>
                            setDraft((p) => ({
                              ...p,
                              items: p.items.map((item) =>
                                item.id === q.id
                                  ? {
                                      ...item,
                                      choices: item.choices.map((ch) =>
                                        ch.id === c.id ? { ...ch, participantAudience: null } : ch,
                                      ),
                                    }
                                  : item,
                              ),
                            }))
                          }
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                            !choiceHasParticipantAudienceRestriction(c)
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          不限
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
              {q.choices.length < MAX_REGISTRATION_OPTION_CHOICES ? (
                <button
                  type="button"
                  onClick={() =>
                    setDraft((p) => ({
                      ...p,
                      items: p.items.map((item) =>
                        item.id === q.id
                          ? { ...item, choices: [...item.choices, emptyRegistrationOptionChoice()] }
                          : item,
                      ),
                    }))
                  }
                  className="w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 py-2 text-xs font-semibold text-teal-600 cursor-pointer hover:border-teal-300 hover:bg-teal-50/40"
                >
                  + 选项
                </button>
              ) : null}
            </div>
          ))}
          {draft.items.length < MAX_REGISTRATION_OPTION_QUESTIONS ? (
            <button
              type="button"
              onClick={() =>
                setDraft((p) => ({ ...p, items: [...p.items, emptyRegistrationOptionQuestion()] }))
              }
              className="w-full flex items-center justify-center rounded-xl border border-dashed border-teal-200 bg-teal-50/40 py-2.5 text-xs font-semibold text-teal-700 cursor-pointer hover:bg-teal-50"
            >
              + 题目
            </button>
          ) : null}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-[2] py-2.5 text-sm font-bold text-white bg-teal-600 rounded-xl hover:bg-teal-700 cursor-pointer"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
